import React from "react";
import type { BillingCycleId } from "../types/billing.types";

export interface CycleOption {
  id: BillingCycleId;
  /** What the pill says — "Monthly", "Yearly (-10%)". The saving in the
   *  brackets is worked out from the price list rather than written down; see
   *  `yearlyDiscountLabel`. */
  label: string;
}

interface CycleToggleProps {
  options: CycleOption[];
  value: BillingCycleId;
  onChange: (cycle: BillingCycleId) => void;
}

/**
 * Monthly or yearly, as one control.
 *
 * The trial offers one product on two cycles, and with a single card on screen
 * the choice has to live somewhere — so it is a switch above the price rather
 * than the two side-by-side cards the Billing tab draws. Same decision, half
 * the screen, and the figures under it change as it moves.
 *
 * A radio group rather than two buttons: this is one question with two answers
 * and only one of them can hold, which is what arrow keys and the announced
 * "2 of 2" both depend on. `role="radio"` on a `<button>` keeps the pill's own
 * focus ring and hover, which a bare `<input>` would not give without being
 * hidden and re-drawn.
 */
const CycleToggle: React.FC<CycleToggleProps> = ({
  options,
  value,
  onChange,
}) => (
  <div
    role="radiogroup"
    aria-label="Billing cycle"
    className="inline-flex items-center gap-1 rounded-full border border-[#BEEFF4] bg-white p-1"
  >
    {options.map((option) => {
      const selected = option.id === value;

      return (
        <button
          key={option.id}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.id)}
          className={`rounded-full px-4 py-1.5 text-[11px] min-[1400px]:text-[12px] 3xl:text-[14px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 ${
            selected
              ? "bg-[#D7F9FD] font-medium text-[#00424B]"
              : "text-ink-muted hover:text-ink"
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export default CycleToggle;
